import { FC, useState } from 'react'
import { PrescricaoType, IconPaths } from '_metronic/helpers'
import { SituacaoHistorico, agruparPorMes, situacaoHistorico } from '../../core/montagem/historico'
import { ItemPrescricao } from '../../core/montagem/itens'
import { PrescricaoList } from '../../core/_model'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { useRotulos } from '../comum/useRotulos'
import { CartaoHistorico } from './CartaoHistorico'
import { Button, CustomSelect, IOption } from 'app/components'

type FiltroTipo = 'todas' | PrescricaoType
type FiltroSituacao = 'todas' | SituacaoHistorico

const TIPOS: FiltroTipo[] = [
  'todas',
  PrescricaoType.Medicamento,
  PrescricaoType.Especial,
  PrescricaoType.Exame,
  PrescricaoType.Formula,
]
const SITUACOES: FiltroSituacao[] = ['todas', 'assinada', 'utilizada', 'vencida']

type IProps = {
  prescricoes: PrescricaoList[]
  /** Estável e sempre atual (`useModal` guarda as props da abertura). */
  onAdicionar: (itens: ItemPrescricao[]) => number
}

/**
 * Conteúdo do "Ver todas", aberto pelo `useModal`: prescrições anteriores agrupadas por mês,
 * com filtro por tipo e situação. Título e fechar ficam no cabeçalho padrão do modal.
 */
export const TodasPrescricoes: FC<IProps> = ({ prescricoes, onAdicionar }) => {
  const { t } = useRotulos()
  const [tipo, setTipo] = useState<FiltroTipo>('todas')
  const [situacao, setSituacao] = useState<FiltroSituacao>('todas')

  const opcoesSituacao: IOption<FiltroSituacao>[] = SITUACOES.map((opcao) => ({
    value: opcao,
    label:
      opcao === 'todas'
        ? t('prescricao.historico.tipo.todas')
        : t(`prescricao.historico.situacao.${opcao}`),
  }))

  const filtradas = prescricoes.filter(
    (prescricao) =>
      (tipo === 'todas' || prescricao.tipo === tipo) &&
      (situacao === 'todas' || situacaoHistorico(prescricao) === situacao)
  )
  const meses = agruparPorMes(filtradas)

  return (
    <div className='d-flex flex-column gap-4'>
      <p className={`fs-7 m-0 ${styles.textoSecundario}`}>{t('prescricao.historico.so_suas')}</p>
      <div className='d-flex flex-wrap align-items-center gap-4 pb-4 border-bottom'>
        <div
          role='group'
          aria-label={t('prescricao.historico.filtrar_tipo')}
          className='d-flex flex-wrap gap-2'
        >
          {TIPOS.map((opcao) => (
            <Button
              key={opcao}
              className={styles.chip}
              ariaPressed={tipo === opcao}
              onClick={() => setTipo(opcao)}
              unstyled
            >
              {tipo === opcao && <Icone icone={IconPaths.Check} />}
              {t(`prescricao.historico.tipo.${opcao}`)}
            </Button>
          ))}
        </div>
        <div className='d-flex align-items-center gap-2 ms-auto'>
          <label htmlFor='historico-situacao' className='fs-7 fw-semibold text-nowrap'>
            {t('prescricao.historico.situacao_rotulo')}
          </label>
          <div className={styles.filtroSituacao}>
            <CustomSelect
              inputId='historico-situacao'
              options={opcoesSituacao}
              value={opcoesSituacao.find((opcao) => opcao.value === situacao)}
              onChange={(opcao: IOption<FiltroSituacao> | null) =>
                setSituacao(opcao?.value ?? 'todas')
              }
            />
          </div>
        </div>
      </div>
      <div>
        {meses.length === 0 && (
          <p className={styles.textoSecundario}>{t('prescricao.historico.nenhuma_filtro')}</p>
        )}
        {meses.map((mes) => (
          <section key={mes.chave} aria-labelledby={`historico-${mes.chave}`} className='mb-6'>
            <h3
              id={`historico-${mes.chave}`}
              className={`fs-7 fw-bold text-uppercase mb-3 ${styles.textoSecundario}`}
            >
              {mes.rotulo}
            </h3>
            <div className='row g-4'>
              {mes.prescricoes.map((prescricao) => (
                <div key={prescricao.id} className='col-md-6 col-xl-4'>
                  <CartaoHistorico
                    prescricao={prescricao}
                    mostrarItens={false}
                    onAdicionar={onAdicionar}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
